import moment from 'moment'

/**
 * Parses duration string (e.g. "2m 15s", "1h 5m", "01:23:45", "120") into seconds
 * @param {string|number} rawValue
 * @returns {number|null}
 */
export const parseDurationToSeconds = (rawValue) => {
  if (rawValue === undefined || rawValue === null || rawValue === '') return null
  if (typeof rawValue === 'number') return isNaN(rawValue) || rawValue < 0 ? null : rawValue

  const str = String(rawValue).trim().toLowerCase()
  if (!str || str === 'n/a' || str === '-' || str === 'null' || str === 'undefined') return null

  if (/^\d+(\.\d+)?$/.test(str)) {
    const num = parseFloat(str)
    return isNaN(num) ? null : num
  }

  if (str.includes(':')) {
    const parts = str.split(':').map((p) => parseFloat(p.trim()))
    if (parts.some((p) => isNaN(p))) return null
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
    if (parts.length === 2) return parts[0] * 60 + parts[1]
  }

  let totalSeconds = 0
  let matched = false
  const hoursMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours)/)
  if (hoursMatch) {
    totalSeconds += parseFloat(hoursMatch[1]) * 3600
    matched = true
  }
  const minutesMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:m|min|mins|minute|minutes)/)
  if (minutesMatch) {
    totalSeconds += parseFloat(minutesMatch[1]) * 60
    matched = true
  }
  const secondsMatch = str.match(/(\d+(?:\.\d+)?)\s*(?:s|sec|secs|second|seconds)/)
  if (secondsMatch) {
    totalSeconds += parseFloat(secondsMatch[1])
    matched = true
  }

  return matched ? totalSeconds : null
}

/**
 * Formats seconds into human-readable representation in hours and minutes (e.g., "53 hrs 20 mins", "45 mins")
 * @param {number} totalSeconds
 * @returns {string}
 */
export const formatSecondsToReadable = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '0 min'
  const seconds = Math.round(totalSeconds)
  if (seconds <= 0) return '0 min'

  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  const parts = []
  if (h > 0) parts.push(`${h} hr${h > 1 ? 's' : ''}`)
  if (m > 0) parts.push(`${m} min${m > 1 ? 's' : ''}`)
  if (parts.length === 0 && s > 0) parts.push(`${s}s`)
  return parts.join(' ') || '0 min'
}

/**
 * Converts seconds to minutes with 1 decimal
 * @param {number} seconds
 * @returns {number}
 */
export const secondsToMinutes = (seconds) => {
  if (!seconds || isNaN(seconds)) return 0
  return Number((seconds / 60).toFixed(1))
}

/**
 * Standardizes date string into YYYY-MM-DD for sorting and display
 * @param {string} rawDate
 * @returns {{ iso: string, display: string, shortDisplay: string, timestamp: number } | null}
 */
export const parseAndFormatDate = (rawDate) => {
  if (!rawDate) return null
  const str = String(rawDate).trim()
  if (!str || str === 'n/a' || str === 'null' || str === 'undefined' || str === '-') return null

  const parsed = moment(new Date(str))
  if (!parsed.isValid()) return null

  const iso = parsed.format('YYYY-MM-DD')
  return {
    iso,
    display: parsed.format('DD MMM YYYY'),
    shortDisplay: parsed.format('DD MMM'),
    timestamp: moment(iso, 'YYYY-MM-DD').valueOf(),
  }
}

/**
 * Extracts hour (0-23) from a time string (e.g. "23:55:17")
 * @param {string} timeStr
 * @returns {number|null}
 */
export const extractHourFromTime = (timeStr) => {
  if (!timeStr) return null
  const str = String(timeStr).trim()
  const match = str.match(/(\d{1,2}):\d{2}/)
  if (match) {
    const hour = parseInt(match[1], 10)
    if (hour >= 0 && hour <= 23) return hour
  }
  return null
}

/**
 * Transforms raw user report rows into structured analytics data
 * @param {Array<Object>} rows
 * @returns {Object}
 */
export const transformUserReportData = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      kpis: {
        totalRecords: 0,
        uniqueUsers: 0,
        uniqueModules: 0,
        mostUsedModule: 'N/A',
        mostUsedModuleCount: 0,
      },
      dailyActivity: [],
      moduleUsage: [],
      topUsers: [],
      uniqueUsersTrend: [],
      activityByHour: [],
      timeSpentByModule: [],
      hasValidTimeSpent: false,
      isEmpty: true,
    }
  }

  const validRows = []
  const userSet = new Set()
  const moduleSet = new Set()
  const moduleCounts = {}
  const userCounts = {}
  const userNamesByEmail = {}
  const dailyActivityMap = {}
  const dailyUniqueUsersMap = {}
  const hourlyActivityMap = Array.from({ length: 24 }, (_, i) => ({
    hourNumber: i,
    hour: `${String(i).padStart(2, '0')}:00`,
    label: `${String(i).padStart(2, '0')}:00`,
    count: 0,
  }))
  const moduleTimeSpentMap = {}
  let validTimeSpentCount = 0
  let totalTimeSpentSeconds = 0

  rows.forEach((row) => {
    if (!row) return

    const email = (row.email || row.Email || '').trim()
    const rawName = (row.name || row.Name || '').trim()
    const name = rawName || (email ? email.split('@')[0] : 'Unknown User')
    const rawModule = (row.moduleName || row.module || row['Module Name'] || '').trim()
    const moduleName = rawModule ? rawModule.charAt(0).toUpperCase() + rawModule.slice(1) : 'General'
    const loginDateRaw =
      row.loginDate ||
      row.LoginDate ||
      row['Login Date'] ||
      row.login_date ||
      row['login_date'] ||
      row.date ||
      row.Date ||
      row.serverDatePretty ||
      row.serverDate ||
      row.datetime ||
      row.created_at ||
      ''

    const loginTimeRaw =
      row.loginTime ||
      row.LoginTime ||
      row['Login Time'] ||
      row.moduleTime ||
      row['Module Time'] ||
      row.time ||
      row.Time ||
      row.serverTimePretty ||
      ''

    const timeSpentRaw = row.timeSpent || row.TimeSpent || row['Time Spent'] || ''

    let parsedDate = parseAndFormatDate(loginDateRaw)
    if (!parsedDate && loginTimeRaw) {
      parsedDate = parseAndFormatDate(loginTimeRaw)
    }

    let hour = extractHourFromTime(loginTimeRaw)
    if (hour === null && loginDateRaw) {
      hour = extractHourFromTime(loginDateRaw)
    }

    const timeSpentSec = parseDurationToSeconds(timeSpentRaw)

    validRows.push({
      email,
      name,
      moduleName,
      parsedDate,
      hour,
      timeSpentSec,
      raw: row,
    })

    if (email) {
      userSet.add(email)
      userNamesByEmail[email] = name
      userCounts[email] = (userCounts[email] || 0) + 1
    }

    if (moduleName) {
      moduleSet.add(moduleName)
      moduleCounts[moduleName] = (moduleCounts[moduleName] || 0) + 1
    }

    if (parsedDate) {
      const dateKey = parsedDate.iso
      if (!dailyActivityMap[dateKey]) {
        dailyActivityMap[dateKey] = {
          date: dateKey,
          displayDate: parsedDate.shortDisplay,
          fullDisplayDate: parsedDate.display,
          timestamp: parsedDate.timestamp,
          count: 0,
        }
        dailyUniqueUsersMap[dateKey] = {
          date: dateKey,
          displayDate: parsedDate.shortDisplay,
          fullDisplayDate: parsedDate.display,
          timestamp: parsedDate.timestamp,
          users: new Set(),
        }
      }
      dailyActivityMap[dateKey].count += 1
      if (email) {
        dailyUniqueUsersMap[dateKey].users.add(email)
      }
    }

    if (hour !== null && hour >= 0 && hour <= 23) {
      hourlyActivityMap[hour].count += 1
    }

    if (timeSpentSec !== null && timeSpentSec > 0) {
      validTimeSpentCount++
      totalTimeSpentSeconds += timeSpentSec
      if (!moduleTimeSpentMap[moduleName]) {
        moduleTimeSpentMap[moduleName] = {
          module: moduleName,
          totalSeconds: 0,
          recordCount: 0,
        }
      }
      moduleTimeSpentMap[moduleName].totalSeconds += timeSpentSec
      moduleTimeSpentMap[moduleName].recordCount += 1
    }
  })

  let mostUsedModule = 'N/A'
  let mostUsedModuleCount = 0
  Object.keys(moduleCounts).forEach((mod) => {
    if (moduleCounts[mod] > mostUsedModuleCount) {
      mostUsedModuleCount = moduleCounts[mod]
      mostUsedModule = mod
    }
  })

  const kpis = {
    totalRecords: validRows.length,
    uniqueUsers: userSet.size,
    uniqueModules: moduleSet.size,
    mostUsedModule,
    mostUsedModuleCount,
  }

  const dailyActivity = Object.values(dailyActivityMap).sort(
    (a, b) => a.timestamp - b.timestamp
  )

  const totalModuleHits = Object.values(moduleCounts).reduce((acc, c) => acc + c, 0) || 1
  const moduleUsage = Object.keys(moduleCounts)
    .map((mod) => ({
      module: mod,
      count: moduleCounts[mod],
      percentage: Number(((moduleCounts[mod] / totalModuleHits) * 100).toFixed(1)),
    }))
    .sort((a, b) => b.count - a.count)

  const topUsers = Object.keys(userCounts)
    .map((email) => ({
      email,
      name: userNamesByEmail[email] || email,
      count: userCounts[email],
    }))
    .sort((a, b) => b.count - a.count)

  const uniqueUsersTrend = Object.values(dailyUniqueUsersMap)
    .map((d) => ({
      date: d.date,
      displayDate: d.displayDate,
      fullDisplayDate: d.fullDisplayDate,
      timestamp: d.timestamp,
      count: d.users.size,
    }))
    .sort((a, b) => a.timestamp - b.timestamp)

  const activityByHour = hourlyActivityMap

  const hasValidTimeSpent = validTimeSpentCount > 0 && totalTimeSpentSeconds > 0
  const timeSpentByModule = Object.values(moduleTimeSpentMap)
    .map((m) => {
      const avgSec = m.recordCount > 0 ? m.totalSeconds / m.recordCount : 0
      return {
        module: m.module,
        totalSeconds: m.totalSeconds,
        totalMinutes: secondsToMinutes(m.totalSeconds),
        totalFormatted: formatSecondsToReadable(m.totalSeconds),
        avgSeconds: Math.round(avgSec),
        avgFormatted: formatSecondsToReadable(avgSec),
        recordCount: m.recordCount,
      }
    })
    .sort((a, b) => b.totalSeconds - a.totalSeconds)

  return {
    kpis,
    dailyActivity,
    moduleUsage,
    topUsers,
    uniqueUsersTrend,
    activityByHour,
    timeSpentByModule,
    hasValidTimeSpent,
    isEmpty: validRows.length === 0,
  }
}
